/**
 * Frontend-only region hints for IPTV “required country” checks.
 * Never claims verified physical location — VPNs, CGNAT, and resellers can skew results.
 */

import { countryLabel } from './countryLabels';

export type RegionConfidence = 'high' | 'medium' | 'low';

/** Where the primary country code came from (best single label for UI). */
export type RegionSource =
  | 'cf_edge_trace'
  | 'ip_geo_primary'
  | 'ip_geo_fallback'
  | 'composite'
  /** No IP-based country; only timezone / language context. */
  | 'weak_hints'
  | 'none';

export interface DetectedRegion {
  countryCode: string | null;
  countryName: string | null;
  source: RegionSource;
  confidence: RegionConfidence;
  isVpnOrProxy: boolean;
  timezone: string;
  language: string;
  /** Set when the user aborted or the overall budget timed out. */
  lookupError?: string;
  /** Cloudflare trace vs primary IP API disagreed on country. */
  conflictingSignals?: boolean;
}

export type RegionCompatibilityVerdict = 'likely_compatible' | 'likely_blocked' | 'unknown_low_confidence';

export interface RegionCompatibility {
  verdict: RegionCompatibilityVerdict;
  /** Short line for dialogs / toasts. */
  headline: string;
  /** One or two sentences; honest about IP / VPN limits. */
  body: string;
}

function normalizeCountryCode(raw: string | undefined | null): string | null {
  const c = raw?.trim().toUpperCase();
  if (!c || c.length !== 2 || !/^[A-Z]{2}$/.test(c)) return null;
  return c;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof fetch === 'function';
}

function readWeakHints(): Pick<DetectedRegion, 'timezone' | 'language'> {
  let timezone = '';
  let language = '';
  try {
    timezone = typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone ?? '' : '';
  } catch {
    timezone = '';
  }
  try {
    language = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  } catch {
    language = '';
  }
  return { timezone, language };
}

/** Cloudflare edge trace — `loc` is often ISO alpha-2 (not verified identity, edge PoV). */
async function fetchCloudflareTraceCountry(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch('https://1.1.1.1/cdn-cgi/trace', {
      signal,
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const text = await res.text();
    const line = text.split('\n').find((l) => l.startsWith('loc='));
    if (!line) return null;
    return normalizeCountryCode(line.slice(4).trim());
  } catch {
    return null;
  }
}

type IpWhoConnection = { asn?: number; org?: string; isp?: string; domain?: string };
type IpWhoPayload = {
  success?: boolean;
  country?: string;
  country_code?: string;
  connection?: IpWhoConnection;
};

async function fetchIpWho(signal?: AbortSignal): Promise<IpWhoPayload | null> {
  try {
    const res = await fetch('https://ipwho.is/', { signal, cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as IpWhoPayload;
    if (data.success === false) return null;
    return data;
  } catch {
    return null;
  }
}

/** Heuristic: hosting / relay / anonymizer strings often appear on IP whois “connection” fields. */
function inferHostingOrAnonymizer(data: IpWhoPayload | null): boolean {
  if (!data?.connection) return false;
  const blob = [data.connection.isp, data.connection.org, data.connection.domain]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!blob) return false;
  return (
    /\b(vpn|proxy|anonymizer|tor|hosting|host\b|data\s*center|datacenter|colo\b|colocation|cdn\b|cloud\s+(flare|front)|edge\s*node)\b/i.test(
      blob,
    ) ||
    /\b(amazon|google\s*llc|microsoft|oracle|digitalocean|hetzner|ovh|linode|vultr|datacamp|cdn77|fastly|akamai|cloudflare)\b/i.test(
      blob,
    )
  );
}

type GeoJsCountry = { name?: string; country?: string };

async function fetchGeoJsCountry(signal?: AbortSignal): Promise<{ code: string; name: string | null } | null> {
  try {
    const res = await fetch('https://get.geojs.io/v1/ip/country.json', {
      signal,
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as GeoJsCountry;
    const code = normalizeCountryCode(data.country ?? data.name);
    if (!code) return null;
    const name = typeof data.name === 'string' && data.name.trim() ? data.name.trim() : countryLabel(code);
    return { code, name };
  } catch {
    return null;
  }
}

function lowerConfidence(c: RegionConfidence): RegionConfidence {
  if (c === 'high') return 'medium';
  if (c === 'medium') return 'low';
  return 'low';
}

function abortedRegion(hints: ReturnType<typeof readWeakHints>): DetectedRegion {
  return {
    countryCode: null,
    countryName: null,
    source: 'none',
    confidence: 'low',
    isVpnOrProxy: false,
    timezone: hints.timezone,
    language: hints.language,
    lookupError: 'Location check timed out or was cancelled.',
  };
}

/**
 * Layered detection (edge trace + primary IP API + optional fallback), confidence, and VPN/hosting heuristics.
 * At most three network requests in the failure path; first wave uses a single parallel batch of two.
 */
export async function detectRegion(signal?: AbortSignal): Promise<DetectedRegion> {
  const { timezone, language } = readWeakHints();

  if (!isBrowser()) {
    return {
      countryCode: null,
      countryName: null,
      source: 'weak_hints',
      confidence: 'low',
      isVpnOrProxy: false,
      timezone,
      language,
      lookupError: 'Location checks only run in the browser.',
    };
  }

  if (signal?.aborted) {
    return abortedRegion({ timezone, language });
  }

  let traceCode: string | null = null;
  let ipWho: IpWhoPayload | null = null;

  try {
    [traceCode, ipWho] = await Promise.all([fetchCloudflareTraceCountry(signal), fetchIpWho(signal)]);
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      return abortedRegion({ timezone, language });
    }
  }

  if (signal?.aborted) {
    return abortedRegion({ timezone, language });
  }

  const ipCode = normalizeCountryCode(ipWho?.country_code);
  const ipName =
    typeof ipWho?.country === 'string' && ipWho.country.trim()
      ? ipWho.country.trim()
      : ipCode
        ? countryLabel(ipCode)
        : null;
  const hosting = inferHostingOrAnonymizer(ipWho);
  let conflictingSignals = false;

  if (ipCode) {
    let confidence: RegionConfidence = 'medium';
    let source: RegionSource = 'ip_geo_primary';

    if (traceCode) {
      if (traceCode === ipCode) {
        confidence = 'high';
        source = 'composite';
      } else {
        conflictingSignals = true;
        confidence = 'low';
      }
    }

    if (hosting) {
      confidence = lowerConfidence(confidence);
    }

    return {
      countryCode: ipCode,
      countryName: ipName,
      source,
      confidence,
      isVpnOrProxy: hosting,
      timezone,
      language,
      conflictingSignals,
    };
  }

  if (traceCode) {
    return {
      countryCode: traceCode,
      countryName: countryLabel(traceCode),
      source: 'cf_edge_trace',
      confidence: 'high',
      isVpnOrProxy: false,
      timezone,
      language,
    };
  }

  const geo = await fetchGeoJsCountry(signal);
  if (signal?.aborted) {
    return abortedRegion({ timezone, language });
  }

  if (geo) {
    return {
      countryCode: geo.code,
      countryName: geo.name,
      source: 'ip_geo_fallback',
      confidence: 'medium',
      isVpnOrProxy: false,
      timezone,
      language,
    };
  }

  return {
    countryCode: null,
    countryName: null,
    source: 'weak_hints',
    confidence: 'low',
    isVpnOrProxy: false,
    timezone,
    language,
  };
}

/**
 * Compare detected region to the server’s required alpha-2 country.
 * Uses confidence and VPN hints — never implies legal “verification”.
 */
export function assessRegionCompatibility(
  detected: DetectedRegion,
  requiredAlpha2: string,
): RegionCompatibility {
  const req = requiredAlpha2.trim().toUpperCase();
  const reqName = countryLabel(req);

  if (detected.lookupError) {
    return {
      verdict: 'unknown_low_confidence',
      headline: 'Could not finish location hint',
      body: `${detected.lookupError} We never verify your real-world country — this is a best-effort public-IP hint only.`,
    };
  }

  if (!detected.countryCode) {
    return {
      verdict: 'unknown_low_confidence',
      headline: 'Unknown — no public-IP country',
      body: `We could not read a country from your public IP (detected from public IP services failed or were blocked). Browser timezone (${detected.timezone || 'unknown'}) and language (${detected.language || 'unknown'}) are not reliable for country. Required region for this server: ${reqName}.`,
    };
  }

  const fromName = detected.countryName ?? countryLabel(detected.countryCode);
  const mismatch = detected.countryCode !== req;

  if (!mismatch) {
    if (detected.isVpnOrProxy || detected.conflictingSignals || detected.confidence === 'low') {
      return {
        verdict: 'unknown_low_confidence',
        headline: 'Unknown / low confidence',
        body: `Country from public IP matches ${reqName} (${fromName}), but we see VPN/proxy/hosting-style network data, conflicting edge vs IP hints, or low confidence. Possible mismatch due to VPN/proxy — never a verified country.`,
      };
    }
    if (detected.confidence === 'high') {
      return {
        verdict: 'likely_compatible',
        headline: 'Likely compatible',
        body: `Detected from public IP: ${fromName} matches ${reqName}, with an edge trace agreeing. Not a verified identity — VPNs can still spoof region.`,
      };
    }
    return {
      verdict: 'likely_compatible',
      headline: 'Likely compatible',
      body: `Detected from public IP: ${fromName} matches required ${reqName}. Medium confidence (single provider or fallback).`,
    };
  }

  if (detected.confidence === 'high' && !detected.conflictingSignals) {
    return {
      verdict: 'likely_blocked',
      headline: 'Likely blocked',
      body: `Detected from public IP: ${fromName} — required ${reqName}. Strong agreement between signals; provider may still block streams outside ${reqName}.`,
    };
  }

  if (detected.confidence === 'medium') {
    return {
      verdict: 'likely_blocked',
      headline: 'Likely blocked',
      body: `Detected from public IP: ${fromName} vs required ${reqName}. Medium confidence — check VPN or DNS if you believe you are in ${reqName}.`,
    };
  }

  return {
    verdict: 'unknown_low_confidence',
    headline: 'Unknown / low confidence',
    body: `Public IP suggests ${fromName} but required ${reqName}. Edge vs IP signals disagreed, confidence is low, or a VPN/hosting path was inferred — possible mismatch due to VPN/proxy. We never claim a verified country.`,
  };
}
